/**
 * stores/auth
 *
 * The refresh token lives in an HttpOnly cookie (Path=/api/auth/) that page
 * JavaScript cannot read; the access token lives only in memory (see
 * setAccessToken in composables/utils). Each tab gets its own access token by
 * calling /auth/refresh/ with the cookie.
 * **/

import { defineStore } from 'pinia'
import { computed, reactive } from 'vue'
import { useRouter } from 'vue-router'
import { authFetch, setAccessToken } from '@/composables/utils'
import { clearUserStorage, STORAGE_KEY_TOKENS } from '@/lib/storage'
import { consumeIntendedUrl, clearIntendedUrl } from '@/lib/redirectAfterLogin'
import { useQueryChatStore } from '@/stores/queryChat'
import { useCourseUploadStore } from '@/stores/courseUpload'

export interface User {
  id: number
  username: string
  email: string
  isSuperuser: boolean
  isStaff: boolean
  permissions: string[]
}

interface AuthState {
  user: User | null
  isAuthenticated: boolean
}

type AuthMessage = { type: 'logout' } | { type: 'login'; userId: number }

/**
 * TRANSITION: sessions started before the cookie switch kept the refresh token in
 * localStorage. It is traded once for the cookie, and left in place so tabs still
 * running the old bundle keep working; logout clears it. Remove this (and the
 * backend's body-token path) one refresh lifetime (7 days) after deploy.
 */
function readLegacyRefresh(): string | null {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY_TOKENS) ?? 'null')?.refresh ?? null
  } catch {
    return null
  }
}

// credentials: 'include' sends and stores the refresh cookie; in dev the API is
// on another port (cross-origin, same-site).
const authPost = (url: string, body: object = {}, keepalive = false) =>
  fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    keepalive,
  })

const performRequest = async (url: string, body: object = {}) => {
  const res = await authPost(url, body)
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}))
    throw new Error(errorData.detail || 'The request failed. Please try again.')
  }
  return res.json() as Promise<{ access: string }>
}

export const useAuthStore = defineStore('auth', () => {
  const router = useRouter()
  const baseUrl = import.meta.env.VITE_API_URL

  const state: AuthState = reactive({
    user: null,
    isAuthenticated: false,
  })

  // Timer reference for silent refresh
  let refreshTimer: ReturnType<typeof setTimeout> | null = null
  // When this tab last received an access token (client clock, so no skew issues).
  let accessIssuedAt = 0
  // Shared promise so concurrent callers (router guard + authFetch 401 handler)
  // always await the same in-flight refresh rather than issuing two requests.
  let activeRefreshPromise: Promise<void> | null = null
  // The router guard calls restoreSession on every navigation while signed out;
  // only the first one per page load asks the server.
  let restorePromise: Promise<void> | null = null

  // Cross-tab sync. Tabs share the cookie, not the access token, so only
  // login/logout events need broadcasting.
  const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('auth') : null

  const permissions = computed(() => state.user?.permissions || [])

  function hasPermission(permission: string): boolean {
    return permissions.value.includes(permission)
  }

  /**
   * Start a timer to refresh the token automatically before it expires.
   * Access token lifetime is 15 minutes, so we refresh at 14 minutes.
   */
  function startRefreshTimer() {
    if (refreshTimer) clearTimeout(refreshTimer)
    refreshTimer = setTimeout(
      async () => {
        try {
          await refreshToken()
        } catch (e) {
          console.warn('Silent refresh failed, will retry on next request via interceptor.', e)
        }
      },
      14 * 60 * 1000,
    ) // 14 minutes
  }

  function stopRefreshTimer() {
    if (refreshTimer) {
      clearTimeout(refreshTimer)
      refreshTimer = null
    }
  }

  async function _updateStateWithAccess(access: string) {
    setAccessToken(access)
    accessIssuedAt = Date.now()
    startRefreshTimer() // Restart the timer
    await fetchSession()
  }

  /**
   * True when the 15-minute access token is about to expire (or the 14-minute
   * timer was delayed, e.g. by a sleeping laptop). The router guard refreshes
   * only then; otherwise the timer and authFetch's 401 retry keep the token fresh.
   */
  function accessNeedsRefresh(): boolean {
    return Date.now() - accessIssuedAt > 13 * 60 * 1000
  }

  async function fetchSession() {
    try {
      const session = await authFetch('/api/auth/session/')
      if (session && (session as { isAuthenticated: boolean }).isAuthenticated) {
        state.user = {
          id: (session as { id: number }).id,
          username: (session as { username: string }).username,
          email: (session as { email: string }).email,
          isSuperuser: (session as { is_superuser: boolean }).is_superuser,
          isStaff: (session as { is_staff: boolean }).is_staff,
          permissions: (session as { permissions: string[] }).permissions,
        }
        state.isAuthenticated = true
      } else {
        logout()
      }
    } catch (error) {
      console.error('Session fetch failed:', error)
      logout()
    }
  }

  /** Trade the refresh cookie (or, once, a legacy localStorage token) for an access token. */
  async function requestAccess(): Promise<string> {
    try {
      return (await performRequest(`${baseUrl}/auth/refresh/`)).access
    } catch (error) {
      const legacy = readLegacyRefresh()
      if (!legacy) throw error
      // The backend answers this with the HttpOnly cookie.
      return (await performRequest(`${baseUrl}/auth/refresh/`, { refresh: legacy })).access
    }
  }

  /** Resume the browser's session on page load. Signed out → resolves quietly. */
  function restoreSession(): Promise<void> {
    restorePromise ??= requestAccess().then(_updateStateWithAccess, () => {})
    return restorePromise
  }

  async function _completeLogin(access: string) {
    await _updateStateWithAccess(access)
    if (state.user) channel?.postMessage({ type: 'login', userId: state.user.id } as AuthMessage)
  }

  async function login(username: string, password: string): Promise<void> {
    // The response also sets the refresh cookie; its body "refresh" is ignored.
    const { access } = await performRequest(`${baseUrl}/auth/login/`, { username, password })
    await _completeLogin(access)
    const intendedUrl = consumeIntendedUrl()
    router.push(intendedUrl ?? '/')
  }

  /**
   * Log in with an access token redeemed from an external-access link; the
   * redeem response has already set the refresh cookie.
   */
  async function loginWithAccess(access: string): Promise<void> {
    await _completeLogin(access)
    clearIntendedUrl()
    router.push('/')
  }

  async function refreshToken(): Promise<void> {
    if (activeRefreshPromise) return activeRefreshPromise

    activeRefreshPromise = (async () => {
      try {
        await _updateStateWithAccess(await requestAccess())
      } catch (error) {
        console.error('Refresh fallito:', error)
        logout()
        throw error
      }
    })().finally(() => {
      activeRefreshPromise = null
    })

    return activeRefreshPromise
  }

  /**
   * Sign out of this browser. `revoke: false` is for logouts mirrored from
   * another tab: that tab already revoked, and the cookie may by now belong to a
   * different user who must not be signed out.
   */
  function logout({ revoke = true } = {}): void {
    stopRefreshTimer()

    // Revoke server-side before forgetting anything. Fire-and-forget (the
    // endpoint always answers 204) so a slow network never blocks sign-out;
    // keepalive lets the request finish while the page navigates to /login.
    if (revoke) {
      const legacy = readLegacyRefresh()
      authPost(`${baseUrl}/auth/logout/`, legacy ? { refresh: legacy } : {}, true).catch(() => {})
      channel?.postMessage({ type: 'logout' } as AuthMessage)
    }

    // Reset in-memory state of stores that hold user-specific data.
    // Called lazily inside the function to avoid circular import issues.
    useQueryChatStore().clearPersistedState()
    useCourseUploadStore().clearAllData()

    // Wipe all user-specific localStorage entries in one pass
    // (legacy tokens, chat history, course draft, all filter keys).
    clearUserStorage()
    clearIntendedUrl()

    setAccessToken(null)
    state.user = null
    state.isAuthenticated = false

    router.push('/login')
  }

  /**
   * - Logout in another tab → sign this tab out too.
   * - Another tab signed in as a different user → sign this tab out rather than
   *   let its next refresh silently switch it to that user.
   */
  channel?.addEventListener('message', (event: MessageEvent<AuthMessage>) => {
    if (!state.isAuthenticated) return
    const msg = event.data
    if (msg.type === 'logout' || (msg.type === 'login' && msg.userId !== state.user?.id)) {
      logout({ revoke: false })
    }
  })

  return {
    user: computed(() => state.user),
    isAuthenticated: computed(() => state.isAuthenticated),
    restoreSession,
    login,
    loginWithAccess,
    refreshToken,
    accessNeedsRefresh,
    logout,
    hasPermission,
  }
})
